import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  darkMediaQuery,
  readMode,
  resolveMode,
  writeMode,
  type ResolvedMode,
  type ThemeMode,
} from '@/shared/config/theme/mode';

type ThemeModeContextValue = {
  /** What the operator chose: `system`, `light` or `dark`. */
  mode: ThemeMode;

  /** What that resolves to right now. */
  resolved: ResolvedMode;

  setMode: (mode: ThemeMode) => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

/**
 * Holds the operator's choice of daylight or dark, and keeps `system` honest.
 *
 * ⚠️ A context rather than a hook each caller runs, for the same reason the locale is one: the
 * choice is made in the navigation rail and applied in `App`, and two independent copies of the
 * state would leave the rail showing one thing while the page paints another.
 */
export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readMode);
  const [resolved, setResolved] = useState<ResolvedMode>(() => resolveMode(readMode()));

  useEffect(() => {
    setResolved(resolveMode(mode));

    if (mode !== 'system') {
      return;
    }

    // ⚠️ `system` follows the machine while the panel is open, not only at load. An operator whose
    // laptop switches to dark at sunset — which is when a search often starts — should not have to
    // reload the screen they are running it from.
    const query = darkMediaQuery();
    if (!query) {
      return;
    }

    const onChange = () => setResolved(resolveMode('system'));

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    writeMode(next);
    setModeState(next);
  }, []);

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode(): ThemeModeContextValue {
  const value = useContext(ThemeModeContext);

  if (!value) {
    throw new Error('useThemeMode must be used inside a ThemeModeProvider.');
  }

  return value;
}
